export const BPS_DENOMINATOR = 10_000;
export const MILLI = 1000;
/* -------------------------------------------------------------------------- */
/* Primitives                                                                  */
/* -------------------------------------------------------------------------- */
/** Rounds half away from zero — the convention users expect for money. */
export function roundHalfUp(value) {
    return value < 0 ? -Math.round(-value) : Math.round(value);
}
/** `paisa × bps / 10000`, rounded half-up. */
export function applyBps(paisa, bps) {
    return roundHalfUp((paisa * bps) / BPS_DENOMINATOR);
}
/** `unitPricePaisa × qtyMilli / 1000`, rounded half-up. */
export function extendLine(unitPricePaisa, qtyMilli) {
    return roundHalfUp((unitPricePaisa * qtyMilli) / MILLI);
}
/**
 * Splits `total` across `weights` proportionally, guaranteeing the parts sum
 * to exactly `total`. The largest-remainder method: floor every share, then
 * hand the leftover units to the entries with the biggest fractional parts.
 *
 * This is what makes a bill-level discount apportionable across lines without
 * a paisa going missing (docs/SPEC.md §5.3).
 */
export function apportion(total, weights) {
    const n = weights.length;
    if (n === 0)
        return [];
    const weightSum = weights.reduce((a, b) => a + b, 0);
    if (weightSum === 0) {
        // Nothing to weigh by — spread as evenly as integers allow.
        const base = Math.trunc(total / n);
        const out = new Array(n).fill(base);
        let left = total - base * n;
        for (let i = 0; left !== 0; i = (i + 1) % n) {
            const step = left > 0 ? 1 : -1;
            out[i] = (out[i] ?? 0) + step;
            left -= step;
        }
        return out;
    }
    const exact = weights.map((w) => (total * w) / weightSum);
    const floored = exact.map((v) => Math.trunc(v));
    let remainder = total - floored.reduce((a, b) => a + b, 0);
    const order = exact
        .map((v, i) => ({ i, frac: Math.abs(v - Math.trunc(v)) }))
        .sort((a, b) => b.frac - a.frac);
    const step = remainder >= 0 ? 1 : -1;
    for (let k = 0; remainder !== 0 && k < order.length * 2; k++) {
        const idx = order[k % order.length]?.i ?? 0;
        floored[idx] = (floored[idx] ?? 0) + step;
        remainder -= step;
    }
    return floored;
}
/** Rounds a total to the nearest `stepPaisa`. Returns the adjustment, not the total. */
export function roundingAdjustment(totalPaisa, stepPaisa, mode = 'NEAREST') {
    if (stepPaisa <= 1)
        return 0;
    const q = totalPaisa / stepPaisa;
    const rounded = mode === 'UP' ? Math.ceil(q) : mode === 'DOWN' ? Math.floor(q) : roundHalfUp(q);
    return rounded * stepPaisa - totalPaisa;
}
/* -------------------------------------------------------------------------- */
/* Tax                                                                         */
/* -------------------------------------------------------------------------- */
/** Splits a tax-inclusive amount into its net and tax parts. */
export function splitInclusive(grossPaisa, taxRateBps) {
    if (taxRateBps === 0)
        return { netPaisa: grossPaisa, taxPaisa: 0 };
    const net = roundHalfUp((grossPaisa * BPS_DENOMINATOR) / (BPS_DENOMINATOR + taxRateBps));
    return { netPaisa: net, taxPaisa: grossPaisa - net };
}
/** Stage 1: the line on its own, before any bill-level discount. */
export function calcLineBase(line) {
    const effectiveTaxRateBps = line.taxExempt ? 0 : line.taxRateBps;
    const extended = extendLine(line.unitPricePaisa, line.qtyMilli);
    const baseNetPaisa = line.priceMode === 'INCL'
        ? splitInclusive(extended, effectiveTaxRateBps).netPaisa
        : extended;
    const requested = line.discountPaisa ?? 0;
    const discountPaisa = Math.min(Math.max(requested, 0), baseNetPaisa);
    return {
        baseNetPaisa,
        discountPaisa,
        netAfterLineDiscountPaisa: baseNetPaisa - discountPaisa,
        effectiveTaxRateBps,
    };
}
/**
 * The single bill calculator.
 *
 * `PRE_TAX` (default): bill-level discounts are apportioned across lines by
 * net weight and reduce the taxable base — tax is charged on what the customer
 * actually pays for the goods.
 *
 * `POST_TAX`: tax is computed on the full net, and the discount only reduces
 * the gross. Some businesses' agreements require this.
 */
export function calcBill(input) {
    const basis = input.discountBasis ?? 'PRE_TAX';
    const bases = input.lines.map(calcLineBase);
    const subtotalNetPaisa = bases.reduce((a, b) => a + b.baseNetPaisa, 0);
    const lineDiscountPaisa = bases.reduce((a, b) => a + b.discountPaisa, 0);
    const afterLine = bases.map((b) => b.netAfterLineDiscountPaisa);
    const afterLineTotal = afterLine.reduce((a, b) => a + b, 0);
    const requestedBill = Math.max(input.billDiscountPaisa ?? 0, 0);
    const requestedLoyalty = Math.max(input.loyaltyDiscountPaisa ?? 0, 0);
    const billDiscountPaisa = Math.min(requestedBill, afterLineTotal);
    const loyaltyDiscountPaisa = Math.min(requestedLoyalty, afterLineTotal - billDiscountPaisa);
    const totalApportionable = billDiscountPaisa + loyaltyDiscountPaisa;
    const shares = apportion(totalApportionable, afterLine);
    const lines = bases.map((b, i) => {
        const apportioned = shares[i] ?? 0;
        const netForTax = basis === 'PRE_TAX' ? b.netAfterLineDiscountPaisa - apportioned : b.netAfterLineDiscountPaisa;
        const taxPaisa = applyBps(netForTax, b.effectiveTaxRateBps);
        const netPaisa = b.netAfterLineDiscountPaisa - apportioned;
        return {
            baseNetPaisa: b.baseNetPaisa,
            discountPaisa: b.discountPaisa,
            apportionedDiscountPaisa: apportioned,
            netPaisa,
            taxPaisa,
            grossPaisa: netPaisa + taxPaisa,
        };
    });
    const taxableNetPaisa = lines.reduce((a, l) => a + l.netPaisa, 0);
    const taxPaisa = lines.reduce((a, l) => a + l.taxPaisa, 0);
    const deliveryFeePaisa = input.deliveryFeePaisa ?? 0;
    const serviceFeePaisa = input.serviceFeePaisa ?? 0;
    const beforeRounding = taxableNetPaisa + taxPaisa + deliveryFeePaisa + serviceFeePaisa;
    const roundingPaisa = roundingAdjustment(beforeRounding, input.roundingStepPaisa ?? 0, input.roundingMode ?? 'NEAREST');
    return {
        lines,
        subtotalNetPaisa,
        lineDiscountPaisa,
        billDiscountPaisa,
        loyaltyDiscountPaisa,
        taxableNetPaisa,
        taxPaisa,
        deliveryFeePaisa,
        serviceFeePaisa,
        roundingPaisa,
        totalPaisa: beforeRounding + roundingPaisa,
    };
}
/**
 * `stockDelta(v) = oldFulfilledQty(v) − newFulfilledQty(v)`.
 *
 * The single formula every revision case falls out of. Variants with a zero
 * delta are omitted — no movement is written for an unchanged line.
 *
 * Always call this with the IMMEDIATELY SUPERSEDED version's quantities as
 * `oldLines`, never the original — that is what makes a v1→v2→v3 chain
 * self-consistent.
 */
export function computeRevisionStockDeltas(oldLines, newLines) {
    const totals = new Map();
    for (const l of oldLines) {
        const e = totals.get(l.variantId) ?? { old: 0, next: 0 };
        e.old += l.qtyMilli;
        totals.set(l.variantId, e);
    }
    for (const l of newLines) {
        const e = totals.get(l.variantId) ?? { old: 0, next: 0 };
        e.next += l.qtyMilli;
        totals.set(l.variantId, e);
    }
    const out = [];
    for (const [variantId, { old, next }] of totals) {
        const deltaMilli = old - next;
        if (deltaMilli !== 0)
            out.push({ variantId, deltaMilli });
    }
    return out.sort((a, b) => a.variantId.localeCompare(b.variantId));
}
/**
 * Money settlement for a revision: positive = collect from the customer,
 * negative = refund to the customer, zero = nothing to do.
 */
export function computeRevisionSettlement(newTotalPaisa, alreadyPaidPaisa) {
    return newTotalPaisa - alreadyPaidPaisa;
}
/* -------------------------------------------------------------------------- */
/* Weighted-average cost                                                       */
/* -------------------------------------------------------------------------- */
/**
 * `newAvg = (onHand × oldAvg + qtyIn × costIn) / (onHand + qtyIn)`, in paisa,
 * rounded half-up. Quantities are thousandths, so both sides scale identically
 * and the milli factor cancels.
 */
export function newAverageCost(onHandMilli, oldAvgPaisa, qtyInMilli, unitCostInPaisa) {
    const totalMilli = onHandMilli + qtyInMilli;
    if (totalMilli <= 0)
        return unitCostInPaisa;
    const value = onHandMilli * oldAvgPaisa + qtyInMilli * unitCostInPaisa;
    return roundHalfUp(value / totalMilli);
}
/* -------------------------------------------------------------------------- */
/* Formatting (UI/print only — never store a formatted value)                  */
/* -------------------------------------------------------------------------- */
export function formatPaisa(paisa, withSymbol = true) {
    const negative = paisa < 0;
    const abs = Math.abs(paisa);
    const rupees = Math.trunc(abs / 100);
    const remainder = abs % 100;
    const grouped = rupees.toLocaleString('en-PK');
    const body = remainder === 0 ? grouped : `${grouped}.${String(remainder).padStart(2, '0')}`;
    return `${negative ? '-' : ''}${withSymbol ? 'PKR ' : ''}${body}`;
}
export function formatQty(qtyMilli, unit) {
    const negative = qtyMilli < 0;
    const abs = Math.abs(qtyMilli);
    const whole = Math.trunc(abs / MILLI);
    const frac = abs % MILLI;
    const body = frac === 0
        ? String(whole)
        : `${whole}.${String(frac).padStart(3, '0').replace(/0+$/, '')}`;
    return `${negative ? '-' : ''}${body}${unit ? ` ${unit}` : ''}`;
}
/** Parses "1.25" / "1,25" / "1" into thousandths. Returns null on garbage. */
export function parseQty(input) {
    const cleaned = input.trim().replace(',', '.');
    if (!/^-?\d*(\.\d{0,3})?$/.test(cleaned) || cleaned === '' || cleaned === '-')
        return null;
    const negative = cleaned.startsWith('-');
    const [whole = '0', frac = ''] = cleaned.replace('-', '').split('.');
    const milli = Number(whole) * MILLI + Number(frac.padEnd(3, '0'));
    return negative ? -milli : milli;
}
/** Parses "125.50" rupees into paisa. Returns null on garbage. */
export function parseRupees(input) {
    const cleaned = input.trim().replace(/,/g, '');
    if (!/^-?\d*(\.\d{0,2})?$/.test(cleaned) || cleaned === '' || cleaned === '-')
        return null;
    const negative = cleaned.startsWith('-');
    const [whole = '0', frac = ''] = cleaned.replace('-', '').split('.');
    const paisa = Number(whole) * 100 + Number(frac.padEnd(2, '0'));
    return negative ? -paisa : paisa;
}
//# sourceMappingURL=money.js.map